import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { IonButton, IonContent, IonPopover, useIonPopover } from '@ionic/react'
import uniqueId from 'lodash/uniqueId'

import { Flash } from '@/components/atoms/assets/Flash'

import { useRemainingVotes } from '@services/votes'
import { queryClient } from '@lib/queryClient'

const RemainingVoteCountComponent = ({
  className = '',
}: {
  className?: string
}) => {
  const { t } = useTranslation('common')
  const { votes } = useRemainingVotes()
  const uuid = useMemo(() => uniqueId(), [])
  const hasVotedOnce = queryClient.getQueryData(['hasVotedOnce'])
  const [isFirstVotePopoverVisible, setIsFirstVotePopoverVisible] =
    useState<boolean>(false)

  const notInitialRender = useRef(false)

  const [present, dismiss] = useIonPopover(
    <IonContent class="ion-padding">
      <p>{t('contest.firstVote', { balance: votes })}</p>
    </IonContent>,
    {
      onDismiss: (data: unknown, role: string) => dismiss(data, role),
    }
  )

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined = undefined

    if (
      notInitialRender.current &&
      hasVotedOnce &&
      !isFirstVotePopoverVisible
    ) {
      setIsFirstVotePopoverVisible(true)
      present({
        trigger: uuid,
        arrow: false,
      })
      timeout = setTimeout(() => {
        setIsFirstVotePopoverVisible(false), dismiss()
      }, 2000)
    } else {
      notInitialRender.current = true
    }

    return () => {
      timeout && clearTimeout(timeout)
    }
  }, [hasVotedOnce, dismiss, isFirstVotePopoverVisible, uuid, present])

  return (
    <>
      <IonButton className="plain vote-count" id={uuid}>
        <span className="text-xl font-bold">{votes}</span>
        <span className={`text-lg flex items-center ${className}`}>
          <Flash />
        </span>
      </IonButton>
      <IonPopover trigger={uuid} arrow={false}>
        <IonContent class="ion-padding text-white">
          <p>
            <Trans i18nKey="contest.details.popover" value={{ count: votes }}>
              {t('contest.details.popover', {
                count: votes,
              })}
            </Trans>
          </p>
        </IonContent>
      </IonPopover>
    </>
  )
}

export const RemainingVoteCount = memo(RemainingVoteCountComponent)
